import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GridLoader from "react-spinners/GridLoader";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  getFavoriteMediums,
  getPractitionarInfo,
} from "../../../services/client";
import MediumCard from "../appointment/premiumsessions/mediumcard";
import useFavoriteToggle from "../appointment/premiumsessions/useFavoriteToggle";
import useProfessionalBooking from "../appointment/premiumsessions/useProfessionalBooking";
import "./favoritemediums.css";

/**
 * The client's saved mediums, reached from the sidebar.
 *
 * A plain client page: it lists and un-hearts, and nothing more. Booking and the
 * medium profile live on the booking pages under /appointment, so both actions
 * hand off there rather than running the booking flow inside this page.
 *
 * Every action that leaves this page stashes what it needs in sessionStorage and
 * navigates to /appointment with a `from` marker; the booking pages pick it up
 * and take over. Because each of those is a real navigation, browser Back from
 * anywhere in the booking flow returns here.
 */
export default function FavoriteMediums() {
  const { t } = useTranslation("clientMisc");
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [profilePendingId, setProfilePendingId] = useState(null);

  const { toggleFavorite, favoritePendingId } = useFavoriteToggle(
    (mediumId, value) =>
      setFavorites((list) =>
        list.map((medium) =>
          medium.medium_id === mediumId
            ? { ...medium, is_favorite: value }
            : medium
        )
      )
  );

  useEffect(() => {
    getFavoriteMediums()
      .then((res) => {
        setFavorites(res && res.data && res.data.status ? res.data.data : []);
      })
      .catch(() => setFavorites([]))
      .finally(() => setIsLoading(false));
  }, []);

  // Book Now opens the same duration / payment popup the Professional Sessions
  // listing uses. Continue then hands off to the booking pages, which show the
  // professional checklist and open the calendar once it is answered.
  //
  // `service` is the payload the popup was already built from, so sending it
  // along saves the booking pages a second fetch. `mediumSessionInfo` is read at
  // Continue time rather than captured, so it is whichever medium's payload the
  // popup is currently showing.
  const { getBookingLink, mediumSessionInfo, bookingModal } =
    useProfessionalBooking({
      // The booking pages call the real getSubsessions after the checklist.
      getSubsessions: () => {},
      onContinue: (chosen) => {
        sessionStorage.setItem(
          "favoriteBooking",
          JSON.stringify({ medium: chosen, service: mediumSessionInfo })
        );
        window.removeBackdrop && window.removeBackdrop();
        navigate("/appointment?from=favorites-book");
      },
    });

  // The client-side medium profile only exists inside the booking pages, so
  // View Profile hands off there. Fetch the profile FIRST and send it along:
  // arriving empty would park the client on the booking journey loader (the
  // globe animation and its "watch this 3-minute video" caption) until the
  // request came back, which is the wrong page to flash on the way to a profile.
  const viewProfile = (item) => {
    if (profilePendingId !== null) {
      return;
    }
    setProfilePendingId(item.medium_id);

    getPractitionarInfo(item.medium_id)
      .then((res) => {
        if (!res || !res.data || !res.data.status) {
          throw new Error("get-medium-details rejected");
        }
        sessionStorage.setItem(
          "favoriteProfileInfo",
          JSON.stringify(res.data.data)
        );
        navigate(
          `/appointment?from=favorites-profile&selectedMediumId=${item.medium_id}`
        );
      })
      .catch(() => {
        toast.error(t("favoriteMediums.profileError"), {
          position: "top-center",
        });
      })
      .finally(() => setProfilePendingId(null));
  };

  // Straight into the professional journey - the pro checklist, then the medium
  // list. Landing on plain /appointment would show the appointment-type chooser
  // (Professional / Donated / Starter), which this client has already answered
  // by being on the favourites page.
  const browseProfessionals = () =>
    navigate("/appointment?from=favorites-browse");

  // Un-hearting flips the flag rather than splicing the row, so a failed save
  // puts the card back exactly where it was.
  const hearted = favorites.filter((medium) => medium.is_favorite);

  return (
    <div className="favorite-mediums-page">
      <div className="favorite-mediums-head">
        <div>
          <h2>{t("favoriteMediums.title")}</h2>
          <p>{t("favoriteMediums.subtitle")}</p>
        </div>
        {hearted.length > 0 && (
          <button
            type="button"
            className="favorite-mediums-browse"
            onClick={browseProfessionals}
          >
            {t("favoriteMediums.browseProfessionals", "Browse Professional Mediums")}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <GridLoader size={50} color={"#D5A67B"} loading={isLoading} />
          <p className="mt-3">{t("favoriteMediums.loading")}</p>
        </div>
      ) : hearted.length === 0 ? (
        <div className="favorite-mediums-empty">
          <div className="favorite-mediums-empty-ring">
            <svg
              viewBox="0 0 24 24"
              width="44"
              height="44"
              fill="none"
              stroke="#D5A67B"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l8.84 8.84 8.84-8.84a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>
          <h3>{t("favoriteMediums.emptyTitle", "No favorites yet")}</h3>
          <p>{t("favoriteMediums.emptyText")}</p>
          <button type="button" onClick={browseProfessionals}>
            {t("favoriteMediums.browseProfessionals", "Browse Professional Mediums")}
          </button>
        </div>
      ) : (
        <div className="favorite-mediums-grid">
          {hearted.map((item) => (
            <div className="col-md-12 mb-3 col-lg-4" key={item.medium_id}>
              <MediumCard
                item={item}
                onBookNow={getBookingLink}
                onViewProfile={viewProfile}
                onToggleFavorite={toggleFavorite}
                canFavorite
                isTogglingFavorite={favoritePendingId === item.medium_id}
                isOpeningProfile={profilePendingId === item.medium_id}
              />
            </div>
          ))}
        </div>
      )}
      {bookingModal}
    </div>
  );
}
